import { observer } from "mobx-react-lite";
import { FC } from "react";
import styled from "@emotion/styled";
import {
  MDSBadge,
  MDSBadgeVariant,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { SubscriptionPlanKind } from "@/store/subscriptions/types";
import { AccountProfileImage } from "@/components/layout/components/account-profile/account-profile-image";

interface AccountInfoProps {
  profileSize?: number;
  profilePhotoUrl: string | null;
  profileDisplayName: string;
  profileEmailAddress?: string;
  plan?: SubscriptionPlanKind;
}

export const SidebarAccountInfo: FC<AccountInfoProps> = observer(
  ({ profileSize = 24, profilePhotoUrl, profileDisplayName, profileEmailAddress, plan }) => {
    const planBadge = getPlanBadge(plan);

    return (
      <Wrapper>
        <AccountProfileImage
          size={profileSize}
          photoUrl={profilePhotoUrl}
          displayName={profileDisplayName}
          showSyncStatus
        />
        <Column>
          <AccountInfoRow>
            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
              {profileDisplayName}
            </MDSText>
          </AccountInfoRow>
          <Row>
            {profileEmailAddress && (
              <AccountInfoRow>
                <StyledEmailText
                  variant={MDSTextVariant.TextXs}
                  weight={MDSTextWeight.Medium}
                  color={MDSTextColor.Secondary}
                >
                  {profileEmailAddress}
                </StyledEmailText>
              </AccountInfoRow>
            )}
            {planBadge && (
              <MDSBadge label={planBadge.label} variant={planBadge.variant} icon={planBadge.icon} />
            )}
          </Row>
        </Column>
      </Wrapper>
    );
  }
);

function getPlanBadge(
  plan: SubscriptionPlanKind | undefined
): { label: string; variant: MDSBadgeVariant; icon?: MDSIconKind } | null {
  if (!plan || plan === SubscriptionPlanKind.UNKNOWN) return null;

  if (plan === SubscriptionPlanKind.FREE) {
    return { label: "Free", variant: MDSBadgeVariant.Default };
  }

  return {
    label: plan === SubscriptionPlanKind.PROACTIVE ? "Proactive" : "Pro",
    variant: MDSBadgeVariant.Emphasis,
    icon: MDSIconKind.StarSolid,
  };
}

const Wrapper = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  width: "fit-content",
  transition: "all 0.1s ease-in-out",
}));

const AccountInfoRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const Column = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 0 0",
  marginLeft: 6,
  gap: "2px",
});

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledEmailText = styled(MDSText)`
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
`;
